import { ensureFirebaseAuth, uploadEncryptedFile, downloadEncryptedFile, getFirebaseUid } from './firebaseStorage';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';
import type { HorusSigner } from '../signers/types';

const DB_KEY_PREFIX = 'horus-xmtp-dbkey-';
const BACKUP_KEY_PREFIX = 'horus-xmtp-bkkey-';
const BACKUP_VERSION = 1;

// ---- Backup encryption key derivation ----

/**
 * Derive a deterministic 32-byte AES-GCM key from a wallet signature.
 * The same wallet will always produce the same key, making backups
 * portable across devices without storing the key separately.
 *
 * The derived key is cached in localStorage so that subsequent backups
 * (including periodic ones) don't require repeated wallet signatures
 * (which would trigger a popup for non-custodial users every time).
 */
async function deriveBackupKey(signer: HorusSigner): Promise<Uint8Array> {
  const address = signer.getIdentifier().identifier.toLowerCase();
  const cacheKey = BACKUP_KEY_PREFIX + address;

  // Check localStorage cache first — avoids wallet popup on every backup
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const arr = JSON.parse(cached);
      if (Array.isArray(arr) && arr.length === 32) {
        return new Uint8Array(arr);
      }
    } catch { /* corrupted, re-derive */ }
  }

  const message = `horus-xmtp-backup-v1-${address}`;
  const signature = await signer.signMessage(message);
  const hashBuf = await crypto.subtle.digest('SHA-256', signature);
  const key = new Uint8Array(hashBuf);

  // Cache for future backups
  localStorage.setItem(cacheKey, JSON.stringify(Array.from(key)));
  return key;
}

// ---- AES-GCM encrypt / decrypt ----

async function encryptBlob(data: Uint8Array, key: Uint8Array): Promise<{ payload: Uint8Array; nonce: Uint8Array }> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await crypto.subtle.importKey('raw', key, 'AES-GCM', false, ['encrypt']);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, cryptoKey, data);
  return { payload: new Uint8Array(encrypted), nonce };
}

async function decryptBlob(payload: Uint8Array, key: Uint8Array, nonce: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey('raw', key, 'AES-GCM', false, ['decrypt']);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, cryptoKey, payload);
  return new Uint8Array(decrypted);
}

// ---- OPFS file operations ----

/**
 * Recursively read all files from an OPFS directory handle.
 * Builds a flat map using path-like keys (e.g. "subdir/file.db3").
 */
async function readOpfsDir(
  dirHandle: FileSystemDirectoryHandle,
  prefix: string,
): Promise<Map<string, Uint8Array>> {
  const files = new Map<string, Uint8Array>();
  for await (const [name, handle] of (dirHandle as any).entries()) {
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'file') {
      try {
        const file = await handle.getFile();
        const buf = await file.arrayBuffer();
        files.set(path, new Uint8Array(buf));
      } catch {
        // Skip unreadable files
      }
    } else if (handle.kind === 'directory') {
      const subFiles = await readOpfsDir(handle, path);
      subFiles.forEach((v, k) => files.set(k, v));
    }
  }
  return files;
}

/**
 * Export OPFS files for backup.
 * Strategy:
 *   1. Try matching files by wallet address (with and without 0x prefix)
 *   2. If no address-specific files found, export ALL OPFS files
 *      (OPFS is per-origin so all files belong to our app)
 */
async function exportOpfsFiles(address: string): Promise<Map<string, Uint8Array>> {
  try {
    const root = await navigator.storage?.getDirectory?.();
    if (!root) return new Map();

    // Read all files recursively
    const allFiles = await readOpfsDir(root, '');
    if (allFiles.size === 0) return allFiles;

    // Try to filter by address (with and without 0x prefix)
    const addr = address.toLowerCase();
    const addrNoPrefix = addr.startsWith('0x') ? addr.slice(2) : addr;

    const matched = new Map<string, Uint8Array>();
    allFiles.forEach((bytes, name) => {
      const nameLower = name.toLowerCase();
      if (nameLower.includes(addr) || nameLower.includes(addrNoPrefix)) {
        matched.set(name, bytes);
      }
    });

    if (matched.size > 0) return matched;

    // No address-specific files — XMTP may use inbox ID or hash for file names.
    // Export all files since OPFS is per-origin (all files belong to our app).
    return allFiles;
  } catch {
    return new Map();
  }
}

/**
 * Import OPFS files from a backup. Writes files to OPFS before the XMTP client
 * is created so there's no lock contention.
 */
async function importOpfsFiles(files: Map<string, Uint8Array>): Promise<void> {
  const root = await navigator.storage?.getDirectory?.();
  if (!root) throw new Error('OPFS not available');

  const entries = Array.from(files.entries());
  for (let i = 0; i < entries.length; i++) {
    const [name, bytes] = entries[i];

    // Handle subdirectory paths (e.g. "subdir/file.db3") by creating
    // intermediate directories — OPFS doesn't support slashes in names.
    const parts = name.split('/');
    let dir: FileSystemDirectoryHandle = root;
    for (let j = 0; j < parts.length - 1; j++) {
      dir = await dir.getDirectoryHandle(parts[j], { create: true });
    }
    const fileName = parts[parts.length - 1];

    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await (fileHandle as any).createWritable();
    await writable.write(bytes);
    await writable.close();
  }
}

// ---- Packaging: combine OPFS files + dbKey into a single blob ----

interface BackupHeader {
  version: number;
  dbKey: number[];  // 32-byte encryption key as array
  files: { name: string; size: number }[];
}

/**
 * Package OPFS files and dbKey into a single Uint8Array.
 * Format: [4-byte header length][JSON header][file1 bytes][file2 bytes]...
 */
function packBackup(files: Map<string, Uint8Array>, dbKey: Uint8Array): Uint8Array {
  const fileEntries: { name: string; size: number }[] = [];
  const fileBuffers: Uint8Array[] = [];

  files.forEach((bytes, name) => {
    fileEntries.push({ name, size: bytes.length });
    fileBuffers.push(bytes);
  });

  const header: BackupHeader = {
    version: BACKUP_VERSION,
    dbKey: Array.from(dbKey),
    files: fileEntries,
  };

  const headerJson = new TextEncoder().encode(JSON.stringify(header));
  const headerLen = new Uint32Array([headerJson.length]);
  const headerLenBytes = new Uint8Array(headerLen.buffer);

  // Total size: 4 (header length) + header + all file bytes
  let totalSize = 4 + headerJson.length;
  for (const buf of fileBuffers) totalSize += buf.length;

  const result = new Uint8Array(totalSize);
  let offset = 0;

  result.set(headerLenBytes, offset); offset += 4;
  result.set(headerJson, offset); offset += headerJson.length;
  for (const buf of fileBuffers) {
    result.set(buf, offset);
    offset += buf.length;
  }

  return result;
}

/**
 * Unpack a backup blob into OPFS files and dbKey.
 */
function unpackBackup(data: Uint8Array): { files: Map<string, Uint8Array>; dbKey: Uint8Array } {
  const headerLenBuf = new Uint32Array(data.buffer.slice(data.byteOffset, data.byteOffset + 4));
  const headerLen = headerLenBuf[0];

  const headerJson = new TextDecoder().decode(data.slice(4, 4 + headerLen));
  const header: BackupHeader = JSON.parse(headerJson);

  if (header.version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version: ${header.version}`);
  }

  const dbKey = new Uint8Array(header.dbKey);
  const files = new Map<string, Uint8Array>();

  let offset = 4 + headerLen;
  for (const entry of header.files) {
    files.set(entry.name, data.slice(offset, offset + entry.size));
    offset += entry.size;
  }

  return { files, dbKey };
}

// ---- Storage path helper ----

/**
 * Get the Firebase Storage path for backups.
 * Uses Firebase auth UID so that Storage rules can enforce:
 *   request.auth.uid == {uid} in the path
 *
 * - Custodial users: UID = Firebase user ID (from social login)
 * - Non-custodial users: UID = wallet address (from custom token)
 *
 * Must be called after ensureFirebaseAuth().
 */
function normalizePathId(id: string): string {
  // Normalize hex wallet addresses to lowercase to avoid case-sensitive path mismatches.
  return /^0x[0-9a-fA-F]+$/.test(id) ? id.toLowerCase() : id;
}

function getBackupPath(fallbackAddress: string): string {
  const uid = getFirebaseUid();
  const pathId = uid ? normalizePathId(uid) : fallbackAddress.toLowerCase();
  return `xmtp-backups/${pathId}/backup.enc`;
}

function getBackupPaths(fallbackAddress: string): string[] {
  const paths = new Set<string>();
  const addr = fallbackAddress.toLowerCase();
  const uid = getFirebaseUid();

  if (uid) {
    paths.add(`xmtp-backups/${uid}/backup.enc`);
    const normalized = normalizePathId(uid);
    paths.add(`xmtp-backups/${normalized}/backup.enc`);
  }

  paths.add(`xmtp-backups/${addr}/backup.enc`);
  return Array.from(paths);
}

// ---- Public API ----

// Debounce flag to prevent concurrent backups
let backupInProgress = false;

/**
 * Back up the XMTP OPFS database to Firebase Storage.
 * Encrypted with a wallet-derived AES-GCM key for portability across devices.
 */
export async function backupXmtpDatabase(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<void> {
  if (backupInProgress) return;
  backupInProgress = true;

  try {
    const addr = address.toLowerCase();

    const opfsFiles = await exportOpfsFiles(addr);
    if (opfsFiles.size === 0) return;

    const dbKeyRaw = localStorage.getItem(DB_KEY_PREFIX + addr);
    if (!dbKeyRaw) return;

    let dbKey: Uint8Array;
    try {
      const arr = JSON.parse(dbKeyRaw);
      dbKey = new Uint8Array(arr);
    } catch {
      return;
    }

    const packed = packBackup(opfsFiles, dbKey);
    const backupKey = await deriveBackupKey(signer);
    const { payload, nonce } = await encryptBlob(packed, backupKey);

    const uploadData = new Uint8Array(12 + payload.length);
    uploadData.set(nonce, 0);
    uploadData.set(payload, 12);

    await ensureFirebaseAuth(walletAddress || addr);
    const path = getBackupPath(addr);
    await uploadEncryptedFile(path, uploadData);
  } catch (err: any) {
    console.error('[XMTP Backup] Failed:', err?.message || err);
  } finally {
    backupInProgress = false;
  }
}

/**
 * Check if a Firebase backup already exists for this user.
 * Must be called after ensureFirebaseAuth().
 */
export async function hasFirebaseBackup(fallbackAddress: string): Promise<boolean> {
  try {
    const storage = getStorage(getApp());
    const paths = getBackupPaths(fallbackAddress);
    for (const path of paths) {
      try {
        const storageRef = ref(storage, path);
        await getDownloadURL(storageRef);
        return true;
      } catch {
        // Try next path
      }
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Sync local XMTP data to Firebase Storage.
 * Always uploads the current local state to keep Firebase as the source of truth.
 * Called after successful local builds and periodic backups.
 */
export async function ensureFirebaseBackup(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<void> {
  const addr = address.toLowerCase();
  try {
    await ensureFirebaseAuth(walletAddress || addr);
    const uid = getFirebaseUid();
    if (!uid) return;
    await backupXmtpDatabase(address, signer, walletAddress);
  } catch {
    // Backup is non-critical
  }
}

/**
 * Restore the XMTP OPFS database from a Firebase Storage backup.
 * Returns true if restoration was successful, false if no backup exists.
 * Throws on transient errors (auth failure, network, decryption) so the caller can retry.
 */
export async function restoreXmtpDatabase(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<boolean> {
  const addr = address.toLowerCase();
  const logTag = '[XMTP Restore]';

  // ---- Step 1: Authenticate with Firebase ----
  try {
    await ensureFirebaseAuth(walletAddress || addr);
  } catch (authErr: any) {
    console.warn(logTag, 'Firebase auth failed:', authErr?.message || authErr);
    throw new Error('Firebase auth failed during restore');
  }

  // ---- Step 2: Find backup download URL ----
  let downloadUrl: string | null = null;
  const paths = getBackupPaths(addr);
  console.info(logTag, 'Searching backup paths:', paths);

  try {
    const storage = getStorage(getApp());
    for (const path of paths) {
      try {
        const storageRef = ref(storage, path);
        downloadUrl = await getDownloadURL(storageRef);
        console.info(logTag, 'Found backup at:', path);
        break;
      } catch {
        // storage/object-not-found is expected for non-existent paths
      }
    }
  } catch (storageErr: any) {
    console.warn(logTag, 'Firebase Storage access failed:', storageErr?.message || storageErr);
    throw new Error('Firebase Storage access failed during restore');
  }

  if (!downloadUrl) {
    console.info(logTag, 'No backup found at any path.');
    return false;
  }

  // ---- Step 3: Download, decrypt, and import ----
  try {
    const uploadData = await downloadEncryptedFile(downloadUrl);
    if (uploadData.length < 12) {
      console.warn(logTag, 'Downloaded file too small:', uploadData.length);
      return false;
    }

    const nonce = uploadData.slice(0, 12);
    const payload = uploadData.slice(12);
    const backupKey = await deriveBackupKey(signer);
    const packed = await decryptBlob(payload, backupKey, nonce);
    const { files, dbKey } = unpackBackup(packed);

    await importOpfsFiles(files);
    localStorage.setItem(DB_KEY_PREFIX + addr, JSON.stringify(Array.from(dbKey)));

    console.info(logTag, 'Restore successful:', files.size, 'files imported.');
    return true;
  } catch (restoreErr: any) {
    console.error(logTag, 'Download/decrypt/import failed:', restoreErr?.message || restoreErr);
    throw new Error('Backup download/decrypt failed: ' + (restoreErr?.message || restoreErr));
  }
}
